'use client';
import {
  CatButton,
  CatIconZoomIn,
  CatIconZoomOut,
  CatImageGalleryItem,
  CatSlider
} from 'components/blocks-components';
import { useRef, useState } from 'react';
import styles from './product-image-zoom-drawer.module.scss';
import { useGesture } from '@use-gesture/react';
import { diagramZoom, getDragBounds } from 'components/parts-diagram/utils';
import cx from 'classnames';
export interface ProductImageGalleryWithZoomProps {
  image?: {
    src: string;
    alt: string;
    type: string;
    thumbnailSrc: string;
  };
}
const MAX_ZOOM = 7;
const MIN_ZOOM = 1;
const STEP_SCALE = 1;
const DEFAULT_ZOOM = 0;

export const isValidZoom = (newZoom: number) =>
  newZoom >= MIN_ZOOM && newZoom <= MAX_ZOOM;
const ProductImageGalleryWithZoom = ({
  image
}: ProductImageGalleryWithZoomProps) => {
  const imageRef = useRef(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [dragging, setDragging] = useState(false);
  const [dragBounds, setDragBounds] = useState(undefined);
  const [scale, setScale] = useState(MIN_ZOOM);

  const handleZoom = (newZoom: number) => {
    if (isValidZoom(newZoom)) {
      diagramZoom(imageRef.current, newZoom);
      setZoom(newZoom);
      setScale(newZoom);

      const height = imageRef.current.clientHeight;
      const width = imageRef.current.clientWidth;
      const bounds = getDragBounds(height, width, newZoom);
      setDragBounds(bounds);
    }
  };

  const handleDrag = (event) => {
    const shouldDragDiagram = zoom > 1;
    if (shouldDragDiagram) {
      const [offsetX, offsetY] = event.offset;
      imageRef.current.style.transform = `scale3d(${zoom}, ${zoom}, ${zoom}) translate3d(${offsetX}px, ${offsetY}px, 0)`;
    }
  };

  const handlePinch = (state) => {
    const [d] = state.offset;
    const newZoom = d / 100;

    const transform = `scale3d(${newZoom}, ${newZoom}, ${newZoom})`;
    imageRef.current.style.transform = transform;
    setScale(newZoom);
    return newZoom;
  };

  const handlePinchEnd = ({ memo = 1 }) => {
    handleZoom(memo);
  };

  useGesture(
    {
      onDrag: (state) => handleDrag(state),
      onDragEnd: () => setDragging(false),
      onDragStart: () => setDragging(true),
      onPinch: (state) => handlePinch(state),
      onPinchEnd: (state) => handlePinchEnd(state)
    },
    {
      target: imageRef,
      eventOptions: { passive: false },
      drag: { bounds: dragBounds },
      pinch: {
        scaleBounds: { min: 100, max: 700 }
      }
    }
  );

  return (
    <CatImageGalleryItem>
      <div className="position-relative overflow-hidden">
        <div
          className={cx('w-100', styles['image-zoom__drawer-image-conatiner'], {
            [styles['dragging']]: dragging
          })}
          ref={imageRef}
          data-testid="product-image-gallery-drawer"
        >
          <picture>
            <img
              data-part="image"
              alt={image.alt}
              className="h-100 w-auto d-block "
              loading="lazy"
              src={image.src}
              style={{
                pointerEvents: 'none',
                objectFit: 'contain'
              }}
            />
          </picture>
        </div>
        <div className="position-absolute bottom-0 start-0 w-100">
          <div
            className="w-100 d-flex justify-content-between align-items-center px-3 my-1 py-2"
            data-testid="zoom-gallery-drawer-controls"
          >
            <CatButton
              data-testid="zoom-out-btn"
              hideText
              size="lg"
              onClick={() => handleZoom(zoom - STEP_SCALE)}
              disabled={zoom <= MIN_ZOOM}
            >
              <CatIconZoomOut slot="before" />
            </CatButton>
            <CatSlider
              hideMinMaxLabel
              hidePositionValue
              style={{ flex: 0.8 }}
              step={STEP_SCALE}
              data-testid="slider"
              onBlSliderChange={(e) => {
                handleZoom(e.detail?.value);
              }}
              value={scale}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
            />
            <CatButton
              data-testid="zoom-in-btn"
              hideText
              size="lg"
              onClick={() => handleZoom(zoom + STEP_SCALE)}
              disabled={zoom >= MAX_ZOOM}
            >
              <CatIconZoomIn slot="before" />
            </CatButton>
          </div>
        </div>
      </div>
    </CatImageGalleryItem>
  );
};

export default ProductImageGalleryWithZoom;
